// @flow
import React from 'react';
import cx from 'classnames';
import { getIn } from 'formik';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/styles';
import {
    FilledInput,
    FormControl,
    FormHelperText,
    FormLabel,
    OutlinedInput,
    Input,
    InputLabel,
} from '@material-ui/core';
import styles from './styles';

type Props = {
    classes?: Object,
    className?: string,
    disabled?: boolean,
    field: Object,
    form: Object,
    fullWidth?: boolean,
    label?: string,
    labelPlacement: 'default' | 'top',
    margin?: string,
    required?: boolean,
    variant: 'standard' | 'outlined' | 'filled',
};

const useStyles = makeStyles(styles, { name: 'FormikInput' });

const variantComponent = {
    standard: Input,
    filled: FilledInput,
    outlined: OutlinedInput,
};

export default function FormikInput(props: Props) {
    const {
        classes: classesProp,
        className,
        disabled,
        field,
        form,
        fullWidth,
        label,
        labelPlacement,
        margin,
        required,
        variant,
        ...other
    } = props;

    const classes = useStyles(props);
    const { i18n, t } = useTranslation();

    const touched = getIn(form.touched, field.name);
    const error = getIn(form.errors, field.name);

    let errorMessage = null;
    if (touched && error) {
        if (typeof error === 'string') {
            errorMessage = i18n.exists(error) ? t(error) : error;
        } else if ('message' in error) {
            const { message, ...options } = error;
            errorMessage = i18n.exists(message) ? t(message, { ...options }) : message;
        }
    }

    const {
        root: rootClassName,
        label: labelClassName,
        input: inputClassName,
        errorMessage: errorClassName,
        ...otherClasses
    } = classes;

    const LabelComponent = labelPlacement === 'top' ? FormLabel : InputLabel;
    const InputComponent = variantComponent[variant] || Input;

    return (
        <FormControl
          className={cx(rootClassName, className)}
          disabled={disabled}
          error={!!errorMessage}
          fullWidth={fullWidth}
          margin={margin}
          required={required}
          variant={variant}
        >
            {label ? <LabelComponent className={labelClassName}>{label}</LabelComponent> : null}
            <InputComponent
              classes={{ root: inputClassName, ...otherClasses }}
              {...other}
              {...field}
            />
            {errorMessage
                ? <FormHelperText className={errorClassName}>{errorMessage}</FormHelperText>
                : null
            }
        </FormControl>
    );
}

FormikInput.defaultProps = {
    labelPlacement: 'default',
    variant: 'standard',
};
